/**
 * FT-264: Unicode / UTF-8 / Normalization utilities.
 *
 * All multilingual text that is compared, searched, or displayed
 * should be NFC-normalized so that characters composed differently
 * (e.g. Hindi / Telugu input from different IMEs) produce identical
 * code-point sequences before any string operation.
 *
 * Why NFC?
 *  - Web browsers normalise user input to NFC by default (HTML spec §4.10.18.7).
 *  - PostgreSQL text columns store whatever byte sequence they receive — if the
 *    client ever bypasses the browser (e.g. a test script, a mobile IME that
 *    emits NFD), stored values could differ from searched values.
 *  - Normalising at the UI presentation/comparison layer ensures that search,
 *    highlight and deduplication logic is robust against composition variance.
 *
 * References:
 *  - Unicode Standard Annex #15 – Unicode Normalization Forms
 *    https://unicode.org/reports/tr15/
 *  - MDN: String.prototype.normalize()
 */

/**
 * Returns a trimmed, NFC-normalized copy of `input`.
 *
 * Safe to call with null / undefined — returns an empty string in those cases.
 *
 * @example
 * normalizeText('राम')   // NFC form; safe for comparison
 * normalizeText('  ')    // ''
 * normalizeText(null)    // ''
 */
export function normalizeText(input: string | null | undefined): string {
  if (!input) return '';
  return input.normalize('NFC').trim();
}

/**
 * Returns a lower-cased, NFC-normalized copy of `input`, suitable for
 * case-insensitive comparisons that must also handle non-ASCII scripts.
 *
 * Uses `String.prototype.toLocaleLowerCase()` so that locale-specific
 * case-folding rules (e.g. Turkish dotted/dotless-I) are handled correctly.
 *
 * @example
 * normalizeForSearch('Ráma')         // 'ráma'
 * normalizeForSearch('  रामचंद्र  ') // 'रामचंद्र'
 */
export function normalizeForSearch(input: string | null | undefined): string {
  return normalizeText(input).toLocaleLowerCase();
}
